import { BaseModel, CrudQuery, PaginationResponse } from "@/interfaces/api";
import { PlainObject } from "@/interfaces/common";
import { Crud } from "@/interfaces/form";
import { joinPaths } from "@/utils";
import { clientDelete, clientGet, clientPatch, clientPost } from "./client";

interface Options<T extends BaseModel, C extends PlainObject, U extends PlainObject> {
  relations?: string[];
  extend?: (instance: Crud<T, C, U>) => Partial<Crud<T, C, U>>;
}

export function crud<T extends BaseModel, C extends PlainObject = PlainObject, U extends PlainObject = PlainObject>(
  path: string,
  entityName: string,
  { extend, ...options }: Options<T, C, U> = {},
) {
  async function getOne(id: string, query?: CrudQuery): Promise<T> {
    return clientGet({ path: getParameterizedUrl(query, id) });
  }

  async function findOne(query: CrudQuery): Promise<T | null> {
    const {
      data: [entity],
    } = await clientGet<PaginationResponse<T>>({
      path: getParameterizedUrl(query),
    });

    return entity ?? null;
  }

  async function getMany(query?: CrudQuery, ...params: string[]): Promise<PaginationResponse<T>> {
    return clientGet({ path: getParameterizedUrl(query, ...params) });
  }

  async function create(payload: C | FormData, ...params: string[]): Promise<T> {
    return clientPost({ path: joinPaths(path, ...params), payload });
  }

  async function handleStatus(id: string, completePath: string): Promise<T> {
    return clientPost({ path: joinPaths(path, id, completePath) });
  }

  async function update(id: string, payload: U | FormData): Promise<T> {
    return clientPatch({ path: joinPaths(path, id), payload });
  }

  async function remove(id: string): Promise<void> {
    return clientDelete({ path: joinPaths(path, id) });
  }

  function getParameterizedUrl(query?: CrudQuery, ...params: string[]) {
    const relations = [options.relations, query?.relations].flat().filter(Boolean);

    if (!query && !relations?.length) {
      return joinPaths(path, ...params);
    }

    const clone = structuredClone(query);

    const { extra } = clone ?? {};

    const parameters = new URLSearchParams();

    if (relations?.length) {
      parameters.append("joins", JSON.stringify(relations));
    }

    if (Object.keys(extra ?? {}).length) {
      Object.entries(extra!).forEach(([key, value]) => {
        if (!value) {
          return;
        }

        if (Array.isArray(value)) {
          value.forEach((entry) => parameters.append(key, entry));
        } else {
          parameters.append(key, value);
        }
      });
    }

    delete clone?.extra;
    delete clone?.relations;

    if (clone) {
      Object.entries(clone).forEach(([key, value]) => {
        parameters.append(key, JSON.stringify(value));
      });
    }

    if (parameters.toString()) {
      return `${joinPaths(path, ...params)}?${parameters.toString()}`;
    }

    return `${joinPaths(path, ...params)}`;
  }

  const instance = {
    getOne,
    findOne,
    getMany,
    create,
    update,
    remove,
    handleStatus,
    path,
    name: entityName,
    getParameterizedUrl,
    ...options,
  };

  if (extend) {
    Object.assign(instance, extend(instance));
  }

  return instance;
}
